import { createPortal } from "preact/compat";
import { useLayoutEffect, useRef, useState } from "preact/hooks";
import type { Folders, Place } from "../api";
import { SYSTEM_NAMES } from "../format";
import { Icon } from "./icons";

// Dossier tel que le menu d'actions le voit. Le dossier d'une adresse est sa boîte de
// réception : location inbox + address.
export interface FolderRef {
  kind: "system" | "address" | "custom";
  location: string;
  address?: string;
  label: string;
  total: number;
  unread: number;
  place?: Place;
  notify?: boolean;
}

export type FolderCommand =
  | { kind: "read" }
  | { kind: "move"; to: "archive" | "trash" }
  | { kind: "empty" }
  | { kind: "place"; place: Place }
  | { kind: "notify"; notify: boolean }
  | { kind: "rename" }
  | { kind: "delete" };

export const shortAddress = (address: string) => address.replace(/@robinluiz\.fr$/, "");

// Retrouve un dossier dans la réponse de /api/folders. null : dossier sans menu (Envoyés,
// Brouillons) ou inconnu.
export function folderRef(folders: Folders | null, location: string, address?: string): FolderRef | null {
  if (!folders) return null;
  if (address) {
    const a = folders.addresses.find((x) => x.address === address);
    return a ? { kind: "address", location: "inbox", address, label: shortAddress(address), total: a.total, unread: a.unread, place: a.place, notify: a.notify } : null;
  }
  const f = folders.custom.find((x) => x.id === location);
  if (f) return { kind: "custom", location, label: f.name, total: f.total, unread: f.unread, place: f.place };
  if (!["inbox", "archive", "spam", "trash"].includes(location)) return null;
  const count = folders.system[location] ?? { total: 0, unread: 0 };
  return { kind: "system", location, label: SYSTEM_NAMES[location], total: count.total, unread: count.unread };
}

type Entry = { label: string; command: FolderCommand; danger?: boolean; disabled?: boolean } | "separator";

function entries(f: FolderRef): Entry[] {
  if (f.location === "trash") return [{ label: "Vider la corbeille", command: { kind: "empty" }, danger: true, disabled: f.total === 0 }];
  const list: Entry[] = [{ label: "Tout marquer comme lu", command: { kind: "read" }, disabled: f.unread === 0 }];
  if (f.location !== "archive") list.push({ label: "Tout archiver", command: { kind: "move", to: "archive" }, disabled: f.total === 0 });
  // Pas de « tout supprimer » sur « Tous les e-mails » : trop large pour un seul geste.
  if (f.kind !== "system" || f.location !== "inbox") {
    list.push({ label: "Tout mettre à la corbeille", command: { kind: "move", to: "trash" }, danger: true, disabled: f.total === 0 });
  }
  if (f.kind === "system") return list;
  list.push("separator",
    f.place === "pinned"
      ? { label: "Ne plus épingler", command: { kind: "place", place: "normal" } }
      : { label: "Épingler en haut", command: { kind: "place", place: "pinned" } },
    f.place === "hidden"
      ? { label: "Ne plus masquer", command: { kind: "place", place: "normal" } }
      : { label: "Masquer", command: { kind: "place", place: "hidden" } });
  // Une adresse masquée ne notifie jamais : le réglage ne s'applique qu'une fois réaffichée.
  if (f.kind === "address" && f.place !== "hidden") {
    list.push(f.notify
      ? { label: "Couper les notifications", command: { kind: "notify", notify: false } }
      : { label: "Activer les notifications", command: { kind: "notify", notify: true } });
  }
  if (f.kind === "custom") {
    list.push("separator",
      { label: "Renommer…", command: { kind: "rename" } },
      { label: "Supprimer le dossier", command: { kind: "delete" }, danger: true });
  }
  return list;
}

// Bouton « ⋯ » et son menu. Le menu est placé hors de la barre latérale (portail vers <body>),
// en position fixe calculée depuis le bouton : ni coupé ni déplacé par le défilement de la
// barre, et toujours dans l'écran.
export function FolderMenuButton({ folder, onCommand, class: className = "icon-btn" }: {
  folder: FolderRef;
  onCommand: (folder: FolderRef, command: FolderCommand) => void;
  class?: string;
}) {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  // Sous le bouton, ou au-dessus s'il n'y a pas la place ; recalculé quand la barre défile.
  const place = () => {
    if (!button.current || !menu.current) return;
    const b = button.current.getBoundingClientRect();
    const m = menu.current.getBoundingClientRect();
    const below = b.bottom + 4;
    const top = below + m.height > innerHeight - 8 ? Math.max(8, b.top - m.height - 4) : below;
    const left = Math.min(Math.max(8, b.right - m.width), innerWidth - m.width - 8);
    // Directement sur l'élément, avant l'affichage : le premier choix peut recevoir le focus.
    menu.current.style.top = `${top}px`;
    menu.current.style.left = `${left}px`;
  };
  useLayoutEffect(() => {
    if (!open) return;
    place();
    menu.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus({ preventScroll: true });
  }, [open]);

  // Écouteurs posés dès l'ouverture (et non après l'affichage) : une touche pressée aussitôt
  // n'est pas perdue.
  useLayoutEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const away = (e: Event) => {
      const t = e.target as Node;
      if (!menu.current?.contains(t) && !button.current?.contains(t)) close();
    };
    const keys = (e: KeyboardEvent) => {
      if (e.key === "Escape") { close(); button.current?.focus(); return; }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.preventDefault();
      const items = [...menu.current!.querySelectorAll<HTMLButtonElement>("button:not(:disabled)")];
      const i = items.indexOf(document.activeElement as HTMLButtonElement);
      items[(i + (e.key === "ArrowDown" ? 1 : items.length - 1)) % items.length]?.focus();
    };
    const scrolled = (e: Event) => { if (!menu.current?.contains(e.target as Node)) place(); };
    addEventListener("pointerdown", away, true);
    addEventListener("keydown", keys);
    addEventListener("resize", close);
    addEventListener("scroll", scrolled, true);
    return () => {
      removeEventListener("pointerdown", away, true);
      removeEventListener("keydown", keys);
      removeEventListener("resize", close);
      removeEventListener("scroll", scrolled, true);
    };
  }, [open]);

  return (
    <>
      <button ref={button} class={className} aria-label={`Actions sur « ${folder.label} »`} title="Actions sur le dossier"
        aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        <Icon name="more" />
      </button>
      {open && createPortal(
        <div ref={menu} class="menu folder-menu" role="menu" aria-label={folder.label}>
          {entries(folder).map((e) => e === "separator" ? <hr /> : (
            <button role="menuitem" class={e.danger ? "danger" : undefined} disabled={e.disabled}
              onClick={() => { setOpen(false); onCommand(folder, e.command); }}>{e.label}</button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
